/**
 * useNavGroups — THE single source of truth for app navigation.
 *
 * Every surface that lists pages derives from this module:
 *   - SideNav (desktop sidebar, incl. saved order + favourites)
 *   - TopNav (mobile hamburger menu)
 *   - CommandPalette (Ctrl+K page search)
 *
 * Do NOT hardcode page lists anywhere else — add/move/remove entries here
 * and every nav surface updates together.
 *
 * Conventions:
 *   - `tooltip` is the plain-English QUESTION the page answers
 *     ("Where should new money go?"), not a feature description.
 *   - `requires` uses the existing Phase B capability manifest
 *     (useCapabilities ← /api/account/me/capabilities). Owner tier sees
 *     everything; until the manifest loads, gated items are hidden
 *     (closed-fail). Backend `require_capability` enforces the same rule
 *     server-side where a route guard exists.
 *   - The Overview group is the "wife-safe" guided zone: short jargon-free
 *     labels, strictly what-should-I-do surfaces. Pro/jargon pages live in
 *     the later groups.
 */
import { useCallback, useMemo, useState } from 'react'
import {
  LayoutDashboard, PieChart, Bot, TrendingUp, Sparkles, Landmark,
  Target, Shield, Settings, Activity, MessageSquare, BarChart2,
  Monitor, Newspaper, Wallet, Database, FlaskConical, Eye,
  CalendarDays, LayoutGrid, Brain, MapPin, Filter, BarChart3, CandlestickChart,
  Users, HelpCircle, FileText, BookOpen, Coins, Globe, Sliders, Bookmark, Percent,
  ClipboardList, DollarSign, Gem, Layers, Rocket, Banknote, GitCompare, PiggyBank,
  Compass, UserCog, UserCheck, ScrollText, Briefcase,
  Radar, Map, Zap, Mail, ShieldCheck, Plug, ScanLine,
  KeyRound, Sun, Trophy,
  type LucideIcon } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { useCapabilities } from '@/hooks/useCapabilities'

// ── Types ──────────────────────────────────────────────────────────────────

export type Capability = 'scenarios' | 'recommendations' | 'auto_execute'

export interface NavItem {
  to: string
  label: string
  icon: LucideIcon
  /** The plain-English question this page answers. */
  tooltip: string
  /** Extra search terms for the command palette / nav search. */
  keywords?: string[]
  end?: boolean
  adminOnly?: boolean
  /** Hide from non-tier users — same manifest the backend enforces. */
  requires?: Capability
}

export interface NavGroup { id: string; title: string; items: NavItem[] }

// ── The nav model ──────────────────────────────────────────────────────────
// NOTE (2026-07 consolidation): the following pages intentionally have NO
// entry because they were merged/deleted — do not re-add:
//   /app/optimizer (live route, off-nav — App.tsx calls it canonical;
//   reachable via /app/optimiser redirect), /app/goals (→ life-goals),
//   /app/holdings (→ /app/portfolio), /app/pulse + /app/alpha-brief
//   (→ /app/daily), look-through / RRG / correlation-engine /
//   economic-index (folded into their parent pages), /app/encryption
//   (demo-only, direct URL), /app/markets (redirect).
// NOTE (2026-07-28 runtime sweep): routes still work via direct URL but are
// intentionally NOT in the nav — do not re-add:
//   /app/trades (Trade Log — duplicates Paper Lab's trade log + Journal),
//   /app/snapshots (NAV Snapshots — reads a never-written table; Net Worth
//   already charts daily_snapshots), /app/technical-scanner (broken 500,
//   superseded by the Paper Lab 5-lens scanner).

export const DEFAULT_NAV_GROUPS: NavGroup[] = [
  // ── Overview — the guided, wife-safe zone ────────────────────────────────
  {
    id: 'overview',
    title: 'Overview',
    items: [
      // Today IS the front door now — the index route. The Command Centre
      // moved to its own path; same page, one click away, all its power kept.
      { to: '/app',             label: 'Today',           icon: Sun, end: true,
        tooltip: 'What should I do with my money today?',
        keywords: ['guide', 'daily', 'what to do'] },
      { to: '/app/command-centre', label: 'Command Centre', icon: LayoutDashboard,
        tooltip: 'How is everything doing right now?',
        keywords: ['dashboard', 'home'] },
      { to: '/app/guide',       label: 'Getting Started', icon: HelpCircle,
        tooltip: 'New here — how do I use this?',
        keywords: ['onboarding', 'how to', 'tutorial'] },
      { to: '/app/deploy-cash', label: 'Invest Cash',     icon: Sparkles,
        tooltip: 'Where should new money go?',
        keywords: ['deploy', 'idle cash', 'invest', 'spare money'] },
      { to: '/app/family',      label: 'Family',          icon: Users,
        tooltip: 'Is the whole family on track?',
        keywords: ['household', 'dashboard', 'fire readiness'] },
      { to: '/app/net-worth',   label: 'Net Worth',       icon: Wallet,
        tooltip: 'What are we worth, and where is it?',
        keywords: ['wealth', 'total', 'assets'] },
      { to: '/app/actions',     label: 'Action Center',   icon: ClipboardList,
        tooltip: 'What money is on the table right now?',
        keywords: ['opportunities', 'insights', 'todo'] },
      { to: '/app/alerts',      label: 'Alerts',          icon: Shield,
        tooltip: 'Has anything I care about been triggered?',
        keywords: ['price alerts', 'watchlist alerts', 'notifications'] },
    ],
  },
  // ── Portfolio — what we hold ─────────────────────────────────────────────
  {
    id: 'portfolio',
    title: 'Portfolio',
    items: [
      { to: '/app/portfolio', label: 'Portfolio',      icon: PieChart,
        tooltip: 'What do I own, and how is it doing?',
        keywords: ['holdings', 'positions', 'p&l'] },
      { to: '/app/accounts',  label: 'Fund Accounts',  icon: Wallet,
        tooltip: "What's inside each of my accounts?",
        keywords: ['brokers', 'aj bell', 'hl', 'ig', 'coinbase', 'funds'] },
      { to: '/app/analytics', label: 'Analytics',      icon: BarChart2,
        tooltip: "What's driving my returns?",
        keywords: ['attribution', 'drift', 'sharpe'] },
      { to: '/app/cash',      label: 'Cash',           icon: Banknote,
        tooltip: 'How much cash is sitting idle, and where?',
        keywords: ['savings', 'cash drag', 'accessible'] },
      { to: '/app/property',  label: 'Property',       icon: MapPin,
        tooltip: 'What are our properties worth after mortgages?',
        keywords: ['house', 'real estate', 'btl', 'mortgage'] },
      { to: '/app/dividends', label: 'Dividends',      icon: Banknote,
        tooltip: 'How much income are my holdings paying me?',
        keywords: ['income', 'yield'] },
      { to: '/app/risk',      label: 'Risk Dashboard', icon: Shield,
        tooltip: 'How much could we lose in a bad market?',
        keywords: ['var', 'stress test', 'drawdown'] },
    ],
  },
  // ── Daily Reads — morning surfaces you read, not tools you drive ─────────
  {
    id: 'daily-reads',
    title: 'Daily Reads',
    items: [
      { to: '/app/daily',              label: 'Daily Brief',        icon: Sparkles,
        tooltip: 'What do all the models say this morning?',
        keywords: ['morning', 'brief', 'pulse', 'alpha brief', 'summary'] },
      { to: '/app/catalysts',          label: 'Catalysts',          icon: Zap,
        tooltip: 'What events could move my stocks soon?',
        keywords: ['fda', 'halts', 'earnings', 'events'] },
      { to: '/app/regime',             label: 'Macro Regime',       icon: Activity,
        tooltip: 'Is the market risk-on or risk-off right now?',
        keywords: ['regime', 'risk-on', 'risk-off', 'kelly'] },
      { to: '/app/prediction-markets', label: 'Prediction Markets', icon: Percent,
        tooltip: 'What odds is real money putting on big events?',
        keywords: ['polymarket', 'metaculus', 'kalshi', 'probability'] },
      { to: '/app/research-papers',    label: 'Research Papers',    icon: FileText,
        tooltip: 'What new research is worth my time?',
        keywords: ['arxiv', 'ssrn', 'nber', 'quant papers'] },
      { to: '/app/map',                label: 'Global Map',         icon: Map,
        tooltip: "What's happening around the world right now?",
        keywords: ['world', 'geopolitics', '3d'] },
    ],
  },
  // ── Ideas & Signals — actionable pro surfaces ────────────────────────────
  {
    id: 'ideas-signals',
    title: 'Ideas & Signals',
    items: [
      { to: '/app/trade-ideas',       label: 'Trade Ideas',       icon: Target,
        tooltip: 'What trades look attractive right now?',
        keywords: ['setups', 'buy', 'sell', 'entry', 'stop'],
        requires: 'recommendations' },
      { to: '/app/blotter',           label: 'Idea Blotter',      icon: BookOpen,
        tooltip: 'What happened to the ideas I saved?',
        keywords: ['ledger', 'kept', 'acted', 'closed'],
        requires: 'recommendations' },
      { to: '/app/predictions',       label: 'Predictions',       icon: TrendingUp,
        tooltip: 'What returns do the models forecast?',
        keywords: ['forecast', 'calibrated', 'base rates'],
        requires: 'recommendations' },
      { to: '/app/trend-radar',       label: 'Trend Radar',       icon: Radar,
        tooltip: 'Which names are trending, and how strongly?',
        keywords: ['smc', 'momentum', 'structure', 'velocity'],
        requires: 'recommendations' },
      { to: '/app/chart-analyser',    label: 'Chart Analyser',    icon: ScanLine,
        tooltip: 'What does this chart screenshot actually say?',
        keywords: ['image', 'screenshot', 'upload', 'pattern', 'vision', 'scalp',
                   'entry', 'stop', 'take profit'] },
      { to: '/app/hidden-alpha',      label: 'Hidden Alpha',      icon: Target,
        tooltip: 'Which under-the-radar names are lighting up?',
        keywords: ['sigma', 'obscure', 'spike'],
        requires: 'recommendations' },
      { to: '/app/alpha-lab',         label: 'Alpha Lab',         icon: FlaskConical,
        tooltip: 'Which discovered edges actually survive testing?',
        keywords: ['edges', 'hypotheses', 'oos', 'backtests'],
        requires: 'recommendations' },
      { to: '/app/paper-lab',         label: 'Paper Trading Lab', icon: CandlestickChart,
        tooltip: 'Would day-trading these signals make money — without real cash?',
        keywords: ['alpaca', 'simulated', 'day trading'] },
      { to: '/app/lab-terminal',      label: 'Lab Terminal', icon: Monitor,
        tooltip: 'The dense live terminal view of the paper lab — agents, positions, the decision stream',
        keywords: ['terminal', 'paper', 'dense', 'quant', 'brain'] },
      { to: '/app/competition',       label: 'Competition', icon: Trophy,
        tooltip: 'The Alpaca hackathon agent: its book, its journal, its research verdicts',
        keywords: ['hackathon', 'alpaca', 'contest', 'unman', 'agent'] },
      { to: '/app/competition-test', label: 'Competition Test', icon: FlaskConical,
        tooltip: 'The sandbox account — the same agent rehearses here every tick; never the contest record',
        keywords: ['hackathon', 'sandbox', 'rehearsal', 'paper', 'test'] },
      // The judge-facing surfaces: no login, safe to share. They live
      // OUTSIDE the authenticated shell — clicking navigates away from
      // the app; the browser back button returns.
      { to: '/public/terminal',      label: 'Terminal (public)', icon: Trophy,
        tooltip: 'The dense live terminal view of the contest book — the demo URL, every number real',
        keywords: ['hackathon', 'terminal', 'public', 'demo', 'judges'] },
      { to: '/public/competition',   label: 'Demo Page (public)', icon: Trophy,
        tooltip: 'The judge-readable public page: book, journal, brain, research — no login',
        keywords: ['hackathon', 'public', 'demo', 'judges'] },
      { to: '/app/smart-money',       label: 'Smart Money',       icon: DollarSign,
        tooltip: 'What are insiders, funds and Congress buying?',
        keywords: ['13f', 'form 4', 'congress', 'options flow'],
        requires: 'recommendations' },
      { to: '/app/risk-radar',        label: 'Risk Radar',        icon: Shield,
        tooltip: 'Which of my holdings look risky right now?',
        keywords: ['bearish', 'concentration', 'selling'],
        requires: 'recommendations' },
      { to: '/app/quiver-strategies', label: 'Quiver Strategies', icon: Bookmark,
        tooltip: 'How do Congress and insider copy-strategies perform?',
        keywords: ['quiver', 'insider', 'congress'],
        requires: 'recommendations' },
      { to: '/app/signals',           label: 'Signal Explorer',   icon: Radar,
        tooltip: 'What raw signals are coming in right now?',
        keywords: ['feeds', 'sources', 'feed inventory'] },
      { to: '/app/congress',          label: 'Congress Trading',  icon: Landmark,
        tooltip: 'What are US lawmakers disclosing?',
        keywords: ['congress', 'senate', 'house', 'pelosi', 'stock act',
                   'politician', 'lawmaker', 'disclosure'] },
      { to: '/app/signal-quality',    label: 'Signal Analytics',  icon: Zap,
        tooltip: 'Which signal sources can I actually trust?',
        keywords: ['accuracy', 'heatmap', 'anomalies', 'hype cycle'] },
      { to: '/app/signal-web',        label: 'Signal Web',        icon: Zap,
        tooltip: 'How are signals converging across tickers?',
        keywords: ['graph', 'convergence', 'network'] },
      { to: '/app/intelligence',      label: 'Alt Data',          icon: Database,
        tooltip: 'What is alternative data — satellites, patents, shipping — saying?',
        keywords: ['alt data', 'congress', 'insider', 'lobbying', 'patent', 'sec', 'filing'] },
    ],
  },
  // ── Markets — external market data ───────────────────────────────────────
  {
    id: 'markets',
    title: 'Markets',
    items: [
      { to: '/app/indices',     label: 'Indices',           icon: BarChart3,
        tooltip: 'How are world markets trading today?',
        keywords: ['ftse', 's&p', 'nikkei', 'index'] },
      { to: '/app/macro',       label: 'Macro',             icon: Globe,
        tooltip: 'What are the big economic numbers doing?',
        keywords: ['fred', 'boe', 'ecb', 'inflation', 'rates'] },
      { to: '/app/news',        label: 'News',              icon: Newspaper,
        tooltip: "What's in the financial news?",
        keywords: ['headlines', 'sentiment'] },
      { to: '/app/heatmap',     label: 'Market Heatmap',    icon: MapPin,
        tooltip: 'Which parts of the market are up or down today?',
        keywords: ['tiles', 'sectors'] },
      { to: '/app/calendar',    label: 'Economic Calendar', icon: CalendarDays,
        tooltip: 'What economic events are coming up?',
        keywords: ['releases', 'cb meetings', 'earnings dates'] },
      { to: '/app/sectors',     label: 'Sectors',           icon: PieChart,
        tooltip: 'Which sectors are leading or lagging?',
        keywords: ['rotation', 'momentum'] },
      { to: '/app/forex',       label: 'Forex',             icon: DollarSign,
        tooltip: 'What are exchange rates doing?',
        keywords: ['fx', 'currency', 'gbpusd'] },
      { to: '/app/commodities', label: 'Commodities',       icon: Gem,
        tooltip: 'What are gold, oil and metals doing?',
        keywords: ['gold', 'oil', 'copper'] },
      { to: '/app/crypto',      label: 'Crypto',            icon: Coins,
        tooltip: "What's happening in crypto?",
        keywords: ['bitcoin', 'ethereum', 'solana'] },
    ],
  },
  // ── Research — ticker-level drilldowns ───────────────────────────────────
  {
    id: 'research',
    title: 'Research',
    items: [
      { to: '/app/watchlist',         label: 'Watchlist',         icon: Eye,
        tooltip: "How are the names I'm watching doing?",
        keywords: ['watch', 'alerts'] },
      { to: '/app/chart',             label: 'Charts',            icon: CandlestickChart,
        tooltip: "What does this ticker's chart look like?",
        keywords: ['candlestick', 'indicators', 'ta'] },
      { to: '/app/screener',          label: 'Screener',          icon: Filter,
        tooltip: 'Which stocks pass my filters?',
        keywords: ['fundamental', 'technical', 'filter'] },
      { to: '/app/earnings',          label: 'Earnings',          icon: BarChart2,
        tooltip: "Who reports earnings soon, and what's expected?",
        keywords: ['whisper', 'reports'] },
      { to: '/app/correlation',       label: 'Correlation',       icon: GitCompare,
        tooltip: 'How do these assets move together?',
        keywords: ['pairs', 'cointegration', 'rolling'] },
      { to: '/app/options',           label: 'Options',           icon: Layers,
        tooltip: 'What is the options market pricing in?',
        keywords: ['chain', 'implied move', 'flow'] },
      { to: '/app/volatility',        label: 'Vol Suite',         icon: Activity,
        tooltip: 'How volatile is the market, really?',
        keywords: ['iv rank', 'vix', 'squeeze', 'term structure'] },
      { to: '/app/ipo',               label: 'IPO Tracker',       icon: Rocket,
        tooltip: "What's coming to market soon?",
        keywords: ['listings', 'lockup'] },
      { to: '/app/brain',             label: 'Research Brain',    icon: Brain,
        tooltip: 'What have I already researched and concluded?',
        keywords: ['knowledge graph', 'theses', 'notes'] },
    ],
  },
  // ── Planning & Tax — long-term ───────────────────────────────────────────
  {
    id: 'planning',
    title: 'Planning & Tax',
    items: [
      { to: '/app/planning',          label: 'FIRE Planning',     icon: Target,
        tooltip: 'When can we afford to retire?',
        keywords: ['retirement', 'financial independence', 'shortfall'] },
      { to: '/app/monte-carlo',       label: 'Scenarios',         icon: Sparkles,
        tooltip: 'What happens to the plan if life changes?',
        keywords: ['monte carlo', 'what-if', 'stacker', 'drawdown', 'jurisdictions', 'simulation'],
        requires: 'scenarios' },
      { to: '/app/income',            label: 'Income',            icon: Banknote,
        tooltip: 'How much income will the portfolio pay, and when?',
        keywords: ['projection', 'monthly', 'calendar', 'dividends'] },
      { to: '/app/tax',               label: 'Tax Workspace',     icon: FileText,
        tooltip: 'How much tax do we owe — and how do we cut it?',
        keywords: ['cgt', 'harvest', 'iht', 'inheritance', 'jurisdictions'] },
      { to: '/app/pension',           label: 'Pensions',          icon: PiggyBank,
        tooltip: 'Are our pensions on track and optimised?',
        keywords: ['sipp', 'db', 'defined benefit', 'tax relief'] },
      { to: '/app/relocation',        label: 'Relocation Plan',   icon: Compass,
        tooltip: 'What would moving abroad actually involve?',
        keywords: ['portugal', 'cyprus', 'uae', 'emigrate'] },
      { to: '/app/concentration',     label: 'Concentration',     icon: Filter,
        tooltip: 'Am I betting too much on one name?',
        keywords: ['single name', 'overweight'] },
      { to: '/app/life-goals',        label: 'Life Goals',        icon: Target,
        tooltip: 'Are we on track for the things that matter?',
        keywords: ['goals', 'education', 'travel', 'property goal'] },
      { to: '/app/advisor',           label: 'Advisor Scorecard', icon: UserCheck,
        tooltip: "Has our IFA's advice actually paid off?",
        keywords: ['ifa', 'track record', 'hit rate'] },
      { to: '/app/ifa-meeting',       label: 'IFA Meeting Prep',  icon: Briefcase,
        tooltip: 'What should we ask the IFA at the next meeting?',
        keywords: ['agenda', 'questions', 'adviser'] },
    ],
  },
  // ── Strategy & Execution — acting on ideas ───────────────────────────────
  {
    id: 'strategy',
    title: 'Strategy & Execution',
    items: [
      { to: '/app/alpha-engine',    label: 'Alpha Engine',      icon: Zap,
        tooltip: 'What is the auto-trading engine planning to do?',
        keywords: ['autonomous', 'scan', 'queue'],
        requires: 'auto_execute' },
      { to: '/app/rebalance-queue', label: 'Trade Queue',       icon: ShieldCheck,
        tooltip: 'Which trades are waiting for my approval?',
        keywords: ['pending', 'approve', 'executed'],
        requires: 'auto_execute' },
      { to: '/app/rebalancer',      label: 'Rebalancer',        icon: Sliders,
        tooltip: 'What trades get me back to my target allocation?',
        keywords: ['rebalance', 'allocation', 'drift'],
        requires: 'auto_execute' },
      { to: '/app/models',          label: 'Model Portfolios',  icon: Layers,
        tooltip: 'What would a ready-made portfolio look like?',
        keywords: ['60/40', 'growth', 'income'] },
      { to: '/app/strategies',      label: 'Strategy Library',  icon: Bookmark,
        tooltip: 'Which of my saved strategies is worth running?',
        keywords: ['custom', 'saved'] },
      { to: '/app/backtest',        label: 'Backtest',          icon: FlaskConical,
        tooltip: 'Would this strategy have worked in the past?',
        keywords: ['walk-forward', 'historical'] },
      { to: '/app/advisory',        label: 'Advisor Briefings', icon: Newspaper,
        tooltip: "What's the AI advisor's view of this market?",
        keywords: ['llm', 'briefing', 'stance'] },
    ],
  },
  // ── AI Agents — autonomous analysts ──────────────────────────────────────
  {
    id: 'agents',
    title: 'AI Agents',
    items: [
      { to: '/app/chat',           label: 'Chat ARIA',      icon: MessageSquare,
        tooltip: 'Got a question? Ask the AI about your money.',
        keywords: ['assistant', 'ask', 'llm'] },
      { to: '/app/phd-swarm',      label: 'PhD Swarm',      icon: Brain,
        tooltip: 'What are the AI analysts working on?',
        keywords: ['swarm', 'autonomous'] },
      { to: '/app/quant-research', label: 'Quant Research', icon: BarChart3,
        tooltip: 'Does this hypothesis hold up statistically?',
        keywords: ['statistics', 'hypothesis'] },
      { to: '/app/agents',         label: 'Agent Status',   icon: Bot,
        tooltip: 'Are the AI agents running properly?',
        keywords: ['status', 'last run'] },
      { to: '/app/round-table',    label: 'Round-Table',    icon: Users,
        tooltip: "What's the bull case AND the bear case for this trade?",
        keywords: ['debate', 'devil advocate'] },
      { to: '/app/personas',       label: 'Personas',       icon: UserCog,
        tooltip: 'Which investor personalities should advise me?',
        keywords: ['buffett', 'lynch', 'risk-off'] },
    ],
  },
  // ── Pro Tools — power-user surfaces ──────────────────────────────────────
  {
    id: 'pro-tools',
    title: 'Pro Tools',
    items: [
      { to: '/app/terminal',  label: 'Terminal',       icon: Monitor,
        tooltip: 'Prefer typing? Run everything from a command line (470+ commands).',
        keywords: ['cli', 'console', 'commands'],
        requires: 'recommendations' },
      { to: '/app/workspace', label: 'Multi-Terminal', icon: LayoutGrid,
        tooltip: 'Want several terminals tiled on one screen?',
        keywords: ['workspace', 'tiles', 'layouts'],
        requires: 'recommendations' },
    ],
  },
  // ── Records & Reports ────────────────────────────────────────────────────
  {
    id: 'records',
    title: 'Records & Reports',
    items: [
      { to: '/app/journal', label: 'Journal', icon: BookOpen,
        tooltip: 'Why did I make each trade?',
        keywords: ['notes', 'theses', 'diary'] },
      { to: '/app/reports', label: 'Reports', icon: FileText,
        tooltip: 'How did we do this month and this quarter?',
        keywords: ['pdf', 'monthly', 'quarterly', 'aum'] },
      { to: '/app/backlog', label: 'Backlog', icon: ClipboardList,
        tooltip: 'What platform improvements are queued?',
        keywords: ['kanban', 'bugs', 'ideas'] },
    ],
  },
  // ── Settings & Admin — configuration + plumbing ──────────────────────────
  {
    id: 'admin',
    title: 'Settings & Admin',
    items: [
      { to: '/app/profile',           label: 'Household Profile', icon: UserCog,
        tooltip: 'Who is in the household, and what are our numbers?',
        keywords: ['family', 'ages', 'income', 'assumptions'] },
      { to: '/app/settings',          label: 'App Settings',      icon: Settings,
        tooltip: 'How do I change app preferences?',
        keywords: ['preferences', 'theme'] },
      { to: '/app/notifications',     label: 'Notifications',     icon: Mail,
        tooltip: 'Where do my alerts get sent?',
        keywords: ['email', 'discord', 'webhook', 'routing'] },
      { to: '/app/api-keys',          label: 'API Keys',          icon: KeyRound, adminOnly: true,
        tooltip: 'Are the data-feed keys set up?',
        keywords: ['fred', 'finnhub', 'eia', 'keys'] },
      { to: '/app/ai-connectors',     label: 'AI Connectors',     icon: Plug,
        tooltip: 'Which AI models power the assistant?',
        keywords: ['deepseek', 'qwen', 'gemini', 'grok', 'llm config'] },
      { to: '/app/broker-keys',       label: 'Broker Keys',       icon: KeyRound,
        tooltip: 'Are my broker connections set up?',
        keywords: ['credentials', 'ibkr', 'alpaca', 'coinbase'] },
      { to: '/app/signal-population', label: 'Feed Health',       icon: Database,
        tooltip: 'Which data feeds are actually populating?',
        keywords: ['signal population', 'collect now', 'feeds', 'data quality'] },
      { to: '/app/system-map',        label: 'System Map',        icon: Layers,
        tooltip: 'How does the platform fit together internally?',
        keywords: ['architecture', 'tables'] },
      { to: '/app/help',              label: 'Help',              icon: HelpCircle,
        tooltip: 'How does this all work?',
        keywords: ['faq', 'shortcuts', 'onboarding'] },
      { to: '/app/admin/waitlist',    label: 'Beta Waitlist',     icon: UserCheck, adminOnly: true,
        tooltip: 'Who is waiting for beta access?',
        keywords: ['approve', 'admin', 'register interest'] },
      { to: '/app/admin/audit',       label: 'Audit Log',         icon: ScrollText,
        tooltip: 'Who did what, and when?',
        keywords: ['events', 'security', 'trail'] },
    ],
  },
]

// ── Flat lookups ───────────────────────────────────────────────────────────

export const ITEM_BY_TO: Record<string, NavItem> = Object.fromEntries(
  DEFAULT_NAV_GROUPS.flatMap(g => g.items).map(item => [item.to, item]),
)

export const GROUP_TITLE_BY_ID: Record<string, string> = Object.fromEntries(
  DEFAULT_NAV_GROUPS.map(g => [g.id, g.title]),
)

// ── Capability filtering ───────────────────────────────────────────────────

/**
 * Returns a stable predicate: can THIS user see this nav item?
 * Owner tier sees everything. Until the capability manifest loads,
 * gated items are hidden (closed-fail). Backend route guards enforce
 * the same manifest, so this just keeps the UI honest.
 */
export function useCanSeeNavItem(): (item: NavItem) => boolean {
  const { user } = useAuth()
  const capabilities = useCapabilities((s) => s.capabilities)
  const isAdmin = user?.is_platform_admin
  return useCallback((item: NavItem): boolean => {
    if (item.adminOnly && !isAdmin) return false
    if (!item.requires) return true
    if (!capabilities) return false
    if (capabilities.is_owner) return true
    if (item.requires === 'scenarios') return capabilities.can_see_scenarios
    if (item.requires === 'recommendations') return capabilities.can_see_recommendations
    if (item.requires === 'auto_execute') return capabilities.can_auto_execute
    return false
  }, [capabilities, isAdmin])
}

/**
 * The capability-filtered nav, in default group order. Groups with no
 * visible items are dropped. This is what TopNav's mobile menu and the
 * CommandPalette consume; SideNav layers saved order/favourites on top
 * via useNavLayout below.
 */
export function useNavGroups(): NavGroup[] {
  const canSee = useCanSeeNavItem()
  return useMemo(
    () =>
      DEFAULT_NAV_GROUPS
        .map(g => ({ ...g, items: g.items.filter(canSee) }))
        .filter(g => g.items.length > 0),
    [canSee],
  )
}

// ── Saved order + favourites (SideNav edit mode) ───────────────────────────
// Bumped to v5: 2026-07 regroup — Intelligence split into Daily Reads /
// Ideas & Signals, Pro Tools added, Risk Dashboard → Portfolio, Terminal
// moved out of Overview. Old layouts reference dead group ids, so a hard
// reset is cleaner than a migration.
const LAYOUT_KEY = 'av-nav-layout-v5'

export interface NavLayout {
  groupOrder: string[]
  itemsByGroup: Record<string, string[]>   // groupId → ordered array of item `to`s
  favourites: string[]                      // ordered array of item `to`s
}

function defaultLayout(): NavLayout {
  return {
    groupOrder: DEFAULT_NAV_GROUPS.map(g => g.id),
    itemsByGroup: Object.fromEntries(
      DEFAULT_NAV_GROUPS.map(g => [g.id, g.items.map(i => i.to)]),
    ),
    favourites: [],
  }
}

function mergeLayout(saved: any): NavLayout {
  const base = defaultLayout()
  if (!saved || typeof saved !== 'object') return base
  // Preserve saved group order, append any new groups at the end
  const groupOrder = Array.isArray(saved.groupOrder)
    ? [...saved.groupOrder.filter((id: any) => typeof id === 'string' && GROUP_TITLE_BY_ID[id])]
    : base.groupOrder.slice()
  for (const g of base.groupOrder) {
    if (!groupOrder.includes(g)) groupOrder.push(g)
  }
  // Items — merge saved with defaults (new items get appended to their original group)
  const itemsByGroup: Record<string, string[]> = {}
  const assigned = new Set<string>()
  for (const gid of groupOrder) {
    const savedList = Array.isArray(saved.itemsByGroup?.[gid]) ? saved.itemsByGroup[gid] : []
    const valid = savedList.filter((to: any) => typeof to === 'string' && ITEM_BY_TO[to])
    itemsByGroup[gid] = valid
    valid.forEach((to: string) => assigned.add(to))
  }
  // Any default items not yet assigned — put back into their original group
  for (const g of DEFAULT_NAV_GROUPS) {
    for (const item of g.items) {
      if (!assigned.has(item.to)) {
        if (!itemsByGroup[g.id]) itemsByGroup[g.id] = []
        itemsByGroup[g.id].push(item.to)
        assigned.add(item.to)
      }
    }
  }
  const favourites = Array.isArray(saved.favourites)
    ? saved.favourites.filter((to: any) => typeof to === 'string' && ITEM_BY_TO[to])
    : []
  return { groupOrder, itemsByGroup, favourites }
}

export function useNavLayout() {
  const [layout, setLayout] = useState<NavLayout>(() => {
    try {
      const raw = localStorage.getItem(LAYOUT_KEY)
      if (raw) return mergeLayout(JSON.parse(raw))
    } catch { /* ignore */ }
    return defaultLayout()
  })

  const persist = useCallback((next: NavLayout) => {
    try { localStorage.setItem(LAYOUT_KEY, JSON.stringify(next)) } catch { /* */ }
    setLayout(next)
  }, [])

  const reset = useCallback(() => {
    try { localStorage.removeItem(LAYOUT_KEY) } catch { /* */ }
    setLayout(defaultLayout())
  }, [])

  const moveGroup = useCallback((fromIdx: number, toIdx: number) => {
    if (fromIdx === toIdx) return
    setLayout(prev => {
      const order = [...prev.groupOrder]
      const [moved] = order.splice(fromIdx, 1)
      order.splice(toIdx, 0, moved)
      const next = { ...prev, groupOrder: order }
      try { localStorage.setItem(LAYOUT_KEY, JSON.stringify(next)) } catch { /* */ }
      return next
    })
  }, [])

  const moveItem = useCallback(
    (itemTo: string, fromGroupId: string, targetGroupId: string, targetIdx: number | null) => {
      setLayout(prev => {
        const itemsByGroup = { ...prev.itemsByGroup }
        // Remove from source
        itemsByGroup[fromGroupId] = (itemsByGroup[fromGroupId] || []).filter(t => t !== itemTo)
        // Insert into target
        const targetList = [...(itemsByGroup[targetGroupId] || [])]
        const idx = targetIdx === null ? targetList.length : Math.max(0, Math.min(targetIdx, targetList.length))
        targetList.splice(idx, 0, itemTo)
        itemsByGroup[targetGroupId] = targetList
        const next = { ...prev, itemsByGroup }
        try { localStorage.setItem(LAYOUT_KEY, JSON.stringify(next)) } catch { /* */ }
        return next
      })
    }, [])

  const toggleFavourite = useCallback((itemTo: string) => {
    setLayout(prev => {
      const favourites = prev.favourites.includes(itemTo)
        ? prev.favourites.filter(t => t !== itemTo)
        : [...prev.favourites, itemTo]
      const next = { ...prev, favourites }
      try { localStorage.setItem(LAYOUT_KEY, JSON.stringify(next)) } catch { /* */ }
      return next
    })
  }, [])

  return { layout, persist, reset, moveGroup, moveItem, toggleFavourite }
}
